/* V2 presentation and native-control adapters; Kaching owns pricing and cart state. */
(() => {
  if (window.tapedV2Initialized) return;
  window.tapedV2Initialized = true;
  // Same original glyphs, trimmed to the page/cart icons. Unknown icons added
  // through theme settings or app rendering load the original full face.
  const subsetIcons = new Set('add arrow_back arrow_forward check check_circle circle close credit_card done info keyboard_arrow_down keyboard_arrow_up local_shipping lock redeem remove schedule sell shopping_bag star verified warning'.split(' '));
  function checkIcon(element) {
    if (!element?.matches?.('.material-symbols-outlined')) return;
    const name = element.textContent.trim();
    element.classList.toggle('tv2-icon-full', Boolean(name && !subsetIcons.has(name)));
  }
  function scanIcons(root) {
    checkIcon(root);
    root.querySelectorAll?.('.material-symbols-outlined').forEach(checkIcon);
  }
  const iconObserver = new MutationObserver(records => {
    records.forEach(record => {
      if (record.type === 'characterData') checkIcon(record.target.parentElement);
      else {
        checkIcon(record.target);
        record.addedNodes.forEach(node => { if (node.nodeType === 1) scanIcons(node); });
      }
    });
  });
  scanIcons(document);
  iconObserver.observe(document.documentElement, {childList:true, characterData:true, subtree:true});
  const pressTargets = '.tv2-button,.tv2-header-cta,[data-tv2-reason] .button,[data-taped-v2-product] .product-form__submit,.sticky-atc .button';
  let touchPress;
  function releasePress() {
    touchPress?.element.classList.remove('tv2-is-pressed');
    touchPress = null;
  }
  // Some touch browsers do not hold :active. This changes appearance only;
  // native click/submission handlers still run without interception or delay.
  document.addEventListener('pointerdown', event => {
    if (event.pointerType === 'mouse' || !event.isPrimary) return;
    releasePress();
    const element = event.target.closest(pressTargets);
    if (!element || element.matches(':disabled,[aria-disabled="true"],.loading')) return;
    touchPress = {element, x:event.clientX, y:event.clientY};
    element.classList.add('tv2-is-pressed');
  }, {passive:true});
  document.addEventListener('pointermove', event => {
    if (touchPress && Math.hypot(event.clientX - touchPress.x, event.clientY - touchPress.y) > 12) releasePress();
  }, {passive:true});
  document.addEventListener('pointerup', releasePress, {passive:true});
  document.addEventListener('pointercancel', releasePress, {passive:true});
  window.addEventListener('blur', releasePress);
  let countryRequest;
  const countryCode = value => /^[A-Z]{2}$/.test(value || '') ? value : '';
  const message = (template, name, value) => template.replaceAll(`[${name}]`, String(value));
  function initShipping(root) {
    root.querySelectorAll('[data-tv2-shipping]').forEach(row => {
      if (row.dataset.ready) return;
      row.dataset.ready = 'true';
      // Match the existing theme's next-business-day estimate, including weekends.
      const dispatch = new Date();
      do { dispatch.setDate(dispatch.getDate() + 1); }
      while (dispatch.getDay() === 0 || dispatch.getDay() === 6);
      row.querySelector('[data-tv2-dispatch]').textContent = dispatch.toLocaleDateString(document.documentElement.lang || 'en', {month:'short', day:'numeric'});
      function setCountry(value) {
        const code = countryCode(value);
        if (!code) return;
        const flag = row.querySelector('[data-tv2-country-flag]');
        flag.onload = () => { flag.hidden = false; };
        flag.onerror = () => { flag.hidden = true; };
        flag.src = `https://cdn.shopify.com/static/images/flags/${code.toLowerCase()}.svg?width=40`;
        let name = code === 'GB' ? 'UK' : code;
        try {
          name = new Intl.DisplayNames([document.documentElement.lang || 'en'], {type:'region'}).of(code) || name;
        } catch (_) { /* Older browsers retain the valid country code. */ }
        row.querySelector('[data-tv2-country-label]').textContent = message(row.dataset.destinationLabel || 'Fast shipping to [country]', 'country', name);
      }
      // Liquid's localization.country is authoritative: prices and checkout use
      // this market too. Neither IP suggestions nor stale browser storage may
      // replace it. A raw ?country= parameter need not be accepted by Shopify.
      const marketCountry = countryCode(row.dataset.country);
      if (marketCountry) { setCountry(marketCountry); return; }
      // Shopify resolves the visitor's IP server-side; no third-party geolocation request.
      countryRequest ||= fetch(`${window.Shopify?.routes?.root || '/'}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true`, {
        credentials:'same-origin', signal:AbortSignal.timeout(3500)
      }).then(response => response.ok ? response.json() : null).then(data => data?.detected_values?.country?.handle).catch(() => null);
      countryRequest.then(code => {
        if (row.isConnected) setCountry(countryCode(row.dataset.country) || code);
      });
    });
  }
  function headerClearance() {
    const header = document.querySelector('.section-header');
    if (!header) return 0;
    // Reserve its full height even while hidden: scrolling up reveals it.
    return Math.ceil(header.querySelector('sticky-header')
      ? header.getBoundingClientRect().height
      : Math.max(0, header.getBoundingClientRect().bottom));
  }
  const productLayouts = new Map();
  const refillAdapters = new Map();
  function initRefillPresets(root) {
    root.querySelectorAll('[data-taped-v2-product]').forEach(product => {
      const checkbox = product.querySelector('[data-tv2-refill]');
      const terms = product.querySelector('[data-tv2-refill-terms]');
      if (!checkbox || !terms || refillAdapters.has(product)) return;
      // Existing Kaching deal IDs and Shopify selling plans; never infer from
      // option order or translated month labels. No price/cart inputs are written.
      const bundlePlans = {
        'GHl4': '690745737492', // 1 pack: 4 weeks
        '5dmw': '690745835796', // 4 packs: 16 weeks
        'lcJZ': '690851643668'  // 6 packs: 24 weeks
      };
      const planWeeks = {
        '690745737492': 4, '690745770260': 8, '690745803028': 12,
        '690745835796': 16, '690851643668': 24
      };
      let syncing = false;
      function sync() {
        if (syncing || !product.isConnected) return;
        syncing = true;
        try {
          const modes = product.querySelectorAll('.kaching-bundles__subscriptions .kaching-bundles__bar');
          const mode = modes[checkbox.checked ? 0 : 1];
          if (!mode) return; // App may mount after DOMContentLoaded.
          if (!mode.classList.contains('kaching-bundles__bar--selected')) {
            mode.click();
            return; // Its DOM update schedules the next sync.
          }
          let label = terms.dataset.oneTime || 'One-time purchase. No automatic refills.';
          if (checkbox.checked) {
            const selectedBundle = [...product.querySelectorAll('[data-deal-bar-id]')]
              .find(bar => bar.querySelector('input[type="radio"]')?.checked);
            const select = product.querySelector('.kaching-bundles__selling-plan-select');
            if (!select) return;
            const plan = bundlePlans[selectedBundle?.dataset.dealBarId];
            if (plan && [...select.options].some(option => option.value === plan) && select.value !== plan) {
              select.value = plan;
              select.dispatchEvent(new Event('change', {bubbles:true}));
            }
            const weeks = planWeeks[select.value];
            label = weeks ? message(terms.dataset.recurring || 'Refills every [weeks] weeks. Cancel anytime.', 'weeks', weeks) : (terms.dataset.cancel || 'Cancel anytime');
          }
          if (terms.textContent !== label) terms.textContent = label;
        } finally { syncing = false; }
      }
      // Kaching can replace controls when switching bundles or purchase modes.
      // Wait for its click/change handlers, then select through its own dropdown.
      const schedule = () => queueMicrotask(sync);
      product.addEventListener('change', schedule);
      product.addEventListener('click', schedule);
      const observer = new MutationObserver(sync);
      observer.observe(product, {childList:true, subtree:true, attributes:true, attributeFilter:['class']});
      refillAdapters.set(product, () => {
        observer.disconnect();
        product.removeEventListener('change', schedule);
        product.removeEventListener('click', schedule);
      });
      sync();
    });
  }
  const bundleLocalizers = new Map();
  function initBundleCopy(root) {
    const payload = document.querySelector('[data-tv2-bundle-copy]');
    if (!payload) return;
    const copy = JSON.parse(payload.textContent);
    root.querySelectorAll('[data-taped-v2-product]').forEach(product => {
      if (bundleLocalizers.has(product)) return;
      const selector = '.kaching-bundles__bar-title,.kaching-bundles__bar-subtitle,.kaching-bundles__bar-label,.kaching-bundles__bar-most-popular__content,.kaching-bundles__free-gift__text,.kaching-bundles__subscriptions__title,.kaching-bundles__timer-title';
      function translate() {
        product.querySelectorAll(selector).forEach(element => {
          element.childNodes.forEach(node => {
            if (node.nodeType !== Node.TEXT_NODE) return;
            const source = node.textContent.trim();
            const target = copy[source];
            if (target && target !== source) node.textContent = node.textContent.replace(source, target);
          });
        });
      }
      const observer = new MutationObserver(translate);
      observer.observe(product, {subtree:true, childList:true, characterData:true});
      bundleLocalizers.set(product, () => observer.disconnect());
      translate();
    });
  }
  function initProductLayout(root) {
    root.querySelectorAll('[data-taped-v2-product]').forEach(product => {
      if (productLayouts.has(product)) return;
      const media = product.querySelector('.product__media-wrapper');
      if (!media) return;
      function updateTimerLabel() {
        const singlePack = product.querySelector('[data-deal-bar-id="GHl4"] input[type="radio"]')?.checked === true;
        product.querySelectorAll('.kaching-bundles__timer').forEach(timer => {
          // Only the larger offers include gifts; do not promise them for one pack.
          timer.hidden = singlePack;
          const value = timer.querySelector('.kaching-bundles__timer-value');
          const title = timer.querySelector('.kaching-bundles__timer-title');
          if (!value || !title || !value.textContent.trim()) {
            timer.querySelector('[data-tv2-timer-compact]')?.remove();
            title?.classList.remove('tv2-timer-original');
            return;
          }
          // The app owns the countdown. This is only a shorter mobile label;
          // assistive technology continues to read the original app message.
          let compact = timer.querySelector('[data-tv2-timer-compact]');
          if (!compact) {
            compact = document.createElement('span');
            compact.dataset.tv2TimerCompact = '';
            compact.setAttribute('aria-hidden', 'true');
            timer.append(compact);
          }
          const label = message(product.dataset.timerLabel || 'Order in [time] for free gifts', 'time', value.textContent.trim());
          if (compact.textContent !== label) compact.textContent = label;
          title.classList.add('tv2-timer-original');
        });
      }
      const timerObserver = new MutationObserver(updateTimerLabel);
      timerObserver.observe(product, {childList:true, characterData:true, subtree:true});
      const scheduleTimerLabel = () => queueMicrotask(updateTimerLabel);
      product.addEventListener('change', scheduleTimerLabel);
      product.addEventListener('click', scheduleTimerLabel);
      updateTimerLabel();
      function update() {
        const clearance = headerClearance();
        media.style.setProperty('--tv2-gallery-top', `${clearance + 16}px`);
        // A tall gallery must scroll normally so every part remains reachable.
        media.toggleAttribute('data-tv2-gallery-static', media.offsetHeight + clearance + 32 > window.innerHeight);
      }
      const observer = new ResizeObserver(update);
      observer.observe(media);
      const header = document.querySelector('.section-header');
      if (header) observer.observe(header);
      window.addEventListener('resize', update, {passive:true});
      productLayouts.set(product, () => {
        observer.disconnect();
        timerObserver.disconnect();
        product.removeEventListener('change', scheduleTimerLabel);
        product.removeEventListener('click', scheduleTimerLabel);
        window.removeEventListener('resize', update);
      });
      update();
    });
  }
  function loadVideoPoster(video) {
    const url = video.dataset.tv2VideoPoster;
    if (!url) return;
    video.poster = url;
    delete video.dataset.tv2VideoPoster;
    posterObserver?.unobserve(video);
  }
  const posterObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) loadVideoPoster(entry.target); });
    }, {rootMargin:'600px 0px'}) : null;
  function initVideoPosters(root) {
    root.querySelectorAll('video[data-tv2-video-poster]').forEach(video => {
      if (posterObserver) posterObserver.observe(video);
      else loadVideoPoster(video);
    });
  }
  function init(root = document) {
    initVideoPosters(root);
    initRefillPresets(root);
    initProductLayout(root);
    initBundleCopy(root);
    initShipping(root);
    root.querySelectorAll('[data-tv2-gallery]').forEach(gallery => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const slides = [...gallery.querySelectorAll('[data-tv2-slide]')];
      const dots = [...gallery.querySelectorAll('[data-tv2-dot]')];
      const stage = gallery.querySelector('.tv2-gallery__stage');
      let current = 0;
      function prepare(index) {
        slides[(index + slides.length) % slides.length].querySelectorAll('img').forEach(img => {
          img.fetchPriority = 'low';
          img.loading = 'eager';
        });
      }
      function prepareNeighbours() {
        // Hidden lazy images otherwise start downloading only after a click.
        // Wait for the visible image before spending bandwidth on adjacent slides.
        const visibleImage = slides[current].querySelector('img');
        const index = current;
        const prepareAdjacent = () => {
          if (current !== index) return;
          prepare(index + 1);
          prepare(index - 1);
        };
        if (visibleImage.complete && visibleImage.naturalWidth) prepareAdjacent();
        else visibleImage.addEventListener('load', prepareAdjacent, {once: true});
      }
      function show(index) {
        current = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => { slide.hidden = i !== current; });
        dots.forEach((dot, i) => { if (i === current) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
        const status = gallery.querySelector('[data-tv2-gallery-status]');
        status.textContent = message(message(status.dataset.format || 'Image [index] of [total]', 'index', current + 1), 'total', slides.length);
        slides[current].querySelectorAll('img').forEach(img => {
          img.fetchPriority = 'auto';
          img.loading = 'eager';
        });
        prepareNeighbours();
      }
      prepareNeighbours();
      gallery.querySelector('[data-tv2-prev]').addEventListener('click', () => show(current - 1));
      gallery.querySelector('[data-tv2-next]').addEventListener('click', () => show(current + 1));
      dots.forEach((dot, i) => dot.addEventListener('click', () => show(i)));
      stage.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); show(current + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      let startX = null;
      stage.addEventListener('touchstart', event => { startX = event.touches[0].clientX; }, {passive:true});
      stage.addEventListener('touchend', event => {
        if (startX === null) return;
        const distance = event.changedTouches[0].clientX - startX;
        if (Math.abs(distance) > 45) show(current + (distance < 0 ? 1 : -1));
        startX = null;
      }, {passive:true});
    });
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    const href = link?.getAttribute('href');
    if (href !== '#taped-offer' && !/^#shopify-section-template--\d+__main$/.test(href || '')) return;
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const target = document.getElementById('taped-offer');
    if (!target) return;
    event.preventDefault();
    // Instant positioning; no delayed retries or scroll hijacking. Image boxes reserve space.
    const offset = headerClearance();
    window.scrollTo({top: window.scrollY + target.getBoundingClientRect().top - offset - 16, behavior: 'instant'});
    target.focus({preventScroll: true});
    history.replaceState(null, '', '#taped-offer');
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    bundleLocalizers.forEach((cleanup, product) => {
      if (!event.target.contains(product)) return;
      cleanup();
      bundleLocalizers.delete(product);
    });
    refillAdapters.forEach((cleanup, product) => {
      if (!event.target.contains(product)) return;
      cleanup();
      refillAdapters.delete(product);
    });
    event.target.querySelectorAll('video[data-tv2-video-poster]').forEach(video => posterObserver?.unobserve(video));
    productLayouts.forEach((cleanup, product) => {
      if (!event.target.contains(product)) return;
      cleanup();
      productLayouts.delete(product);
    });
  });
})();
